import React, { useState, useEffect } from "react";
import { Download, Copy, Check, X } from "lucide-react";
import InlineSpinner from "../shared/Spinner/InlineSpinner";
import CustomTooltip from "../Tooltip/CustomTooltip";
import logger from "../../services/Logger";

const MediaModal = ({ src, alt, onClose, type = 'image' }) => {
  const [copied, setCopied] = useState(false);
  const [downloading, setDownloading] = useState(false);

  const handleDownloadImage = async () => {
    if (downloading) return; // Prevent multiple downloads

    try {
      setDownloading(true);
      // cache: 'no-store' is required here — the <img>/<video> tag that displays
      // this same src already loaded it in no-cors mode, caching an opaque
      // response with no CORS metadata under the backend's 24h Cache-Control.
      // A plain fetch() reuses that cached opaque entry and fails the CORS
      // check even though the live server response is valid; no-store forces
      // a fresh network request instead.
      const response = await fetch(src, { cache: 'no-store' });
      const blob = await response.blob();

      // Create download link
      const url = URL.createObjectURL(blob);
      const link = document.createElement('a');

      // Extract filename from src. Our backend download URLs put the real
      // object path in the "file_path" query param (e.g.
      // /storage/download-image?file_path=synapse/.../image_x.png&org_id=...) —
      // the URL's own pathname is just "/storage/download-image", so parsing
      // pathname alone would name the file "download-image.png". Read
      // file_path first; fall back to pathname for other URL shapes (e.g.
      // Firebase Storage signed URLs, which encode the object name in the path).
      const parsedSrc = new URL(src, window.location.origin);
      const filePathParam = parsedSrc.searchParams.get('file_path');
      const filename = (filePathParam || parsedSrc.pathname).split('/').pop() || 'image';
      const extension = blob.type.split('/')[1] || 'png';
      const downloadName = filename.includes('.') ? filename : `${filename}.${extension}`;

      link.setAttribute('href', url);
      link.setAttribute('download', downloadName);
      link.style.visibility = 'hidden';

      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);

      URL.revokeObjectURL(url);
    } catch (err) {
      logger.error('ImageModal', 'Download image failed', { error: err.message });
    } finally {
      setDownloading(false);
    }
  };

  const handleCopyImage = async () => {
    try {
      if (type === 'video') {
        // Videos can't be copied to clipboard — copy the URL instead
        await navigator.clipboard.writeText(src);
        setCopied(true);
        setTimeout(() => setCopied(false), 2000);
        return;
      }

      // For images, copy the image blob to clipboard.
      // cache: 'no-store' avoids reusing the opaque no-cors cache entry left by
      // the <img> tag's display load — see handleDownloadImage for the full
      // explanation of why a plain fetch() fails CORS here.
      const response = await fetch(src, { cache: 'no-store' });
      const blob = await response.blob();

      if (navigator.clipboard?.write) {
        await navigator.clipboard.write([
          new ClipboardItem({
            [blob.type]: blob
          })
        ]);
        setCopied(true);
        setTimeout(() => setCopied(false), 2000);
      } else {
        // Fallback: copy image URL
        await navigator.clipboard.writeText(src);
        setCopied(true);
        setTimeout(() => setCopied(false), 2000);
      }
    } catch (err) {
      logger.error('ImageModal', 'Copy failed', { error: err.message });
      // Fallback to copying URL
      try {
        await navigator.clipboard.writeText(src);
        setCopied(true);
        setTimeout(() => setCopied(false), 2000);
      } catch (urlErr) {
        logger.error('ImageModal', 'Copy URL fallback failed', { error: urlErr.message });
      }
    }
  };

  useEffect(() => {
    const handleKeyPress = (e) => {
      if (e.key === 'Escape') onClose();
    };

    document.addEventListener('keydown', handleKeyPress);
    document.body.style.overflow = 'hidden'; // Prevent background scroll

    return () => {
      document.removeEventListener('keydown', handleKeyPress);
      document.body.style.overflow = 'unset';
    };
  }, [onClose]);

  return (
    <div
      className="fixed inset-0 z-50 bg-surface-overlay/70 backdrop-blur-sm flex items-center justify-center pl-[190px]"
      onClick={onClose}
    >
      {/* Floating action buttons — top right */}
      <div
        className="absolute top-4 right-4 flex items-center gap-2 z-10"
        onClick={(e) => e.stopPropagation()}
      >
        <CustomTooltip content={downloading ? "Downloading..." : "Download"} side="bottom" align="center">
          <button
            onClick={(e) => { e.stopPropagation(); handleDownloadImage(); }}
            disabled={downloading}
            className="flex items-center p-2 bg-fill-black/30 hover:bg-fill-black/50 text-ink-on-color rounded-lg disabled:opacity-50 transition-colors"
          >
            {downloading ? <InlineSpinner /> : <Download className="w-3.5 h-3.5" />}
          </button>
        </CustomTooltip>

        <CustomTooltip content={copied ? "Copied!" : type === 'video' ? "Copy URL" : "Copy image"} side="bottom" align="center">
          <button
            onClick={(e) => { e.stopPropagation(); handleCopyImage(); }}
            className="flex items-center p-2 bg-fill-black/30 hover:bg-fill-black/50 text-ink-on-color rounded-lg transition-colors"
          >
            {copied
              ? <Check className="w-3.5 h-3.5 text-ink-green-400" />
              : <Copy className="w-3.5 h-3.5" />}
          </button>
        </CustomTooltip>

        <button
          onClick={(e) => { e.stopPropagation(); onClose(); }}
          className="flex items-center p-2 bg-fill-black/30 hover:bg-fill-black/50 text-ink-on-color rounded-lg transition-colors"
          aria-label="Close"
        >
          <X className="w-4 h-4" />
        </button>
      </div>

      {/* Media — no container box */}
      <div
        className="w-[calc((100vw-190px)*0.90)] h-[90vh] flex items-center justify-center overflow-auto"
        onClick={(e) => e.stopPropagation()}
      >
        {type === 'video' ? (
          <video
            src={src}
            controls
            autoPlay
            loop
            className="max-w-full max-h-full rounded-2xl"
          />
        ) : (
          <img
            src={src}
            alt={alt}
            className="max-w-full max-h-full object-contain select-none rounded-2xl"
            onDragStart={(e) => e.preventDefault()}
          />
        )}
      </div>
    </div>
  );
};

export default MediaModal;
